import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

// DataDog configuration
const DATADOG_CONFIG = {
  applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID || '',
  clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || '',
  site: (process.env.REACT_APP_DATADOG_SITE || 'datadoghq.com') as 'datadoghq.com' | 'datadoghq.eu' | 'us3.datadoghq.com' | 'us5.datadoghq.com' | 'ap1.datadoghq.com',
  service: 'back-office-client',
  env: process.env.REACT_APP_ENV || 'development',
  version: process.env.REACT_APP_VERSION || '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input' as 'allow' | 'mask' | 'mask-user-input',
};

// Initialize DataDog RUM
export const initDataDogRUM = () => {
  if (!DATADOG_CONFIG.applicationId || !DATADOG_CONFIG.clientToken) {
    console.warn('DataDog RUM not initialized: Missing application ID or client token');
    return;
  }

  try {
    datadogRum.init({
      applicationId: DATADOG_CONFIG.applicationId,
      clientToken: DATADOG_CONFIG.clientToken,
      site: DATADOG_CONFIG.site,
      service: DATADOG_CONFIG.service,
      env: DATADOG_CONFIG.env,
      version: DATADOG_CONFIG.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input' as 'allow' | 'mask' | 'mask-user-input',
      // Enhanced error tracking
      enableExperimentalFeatures: ['feature_flags'],
    });

    // Set up global error handler to capture unhandled errors
    window.addEventListener('error', (event) => {
      // Only log if it's not already handled by our custom logging
      if (!event.error?.__datadogHandled) {
        datadogRum.addError(event.error || new Error(event.message), {
          source: 'window.onerror',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    });

    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      // Only log if it's not already handled by our custom logging
      if (!event.reason?.__datadogHandled) {
        datadogRum.addError(new Error(event.reason), {
          source: 'unhandledrejection',
          reason: event.reason,
        });
      }
    });

    console.log('DataDog RUM initialized successfully');
  } catch (error) {
    console.error('Failed to initialize DataDog RUM:', error);
  }
};

// Initialize DataDog Logs
export const initDataDogLogs = () => {
  if (!DATADOG_CONFIG.clientToken) {
    console.warn('DataDog Logs not initialized: Missing client token');
    return;
  }

  try {
    datadogLogs.init({
      clientToken: DATADOG_CONFIG.clientToken,
      site: DATADOG_CONFIG.site,
      service: DATADOG_CONFIG.service,
      env: DATADOG_CONFIG.env,
      version: DATADOG_CONFIG.version,
      // Disable automatic error forwarding to avoid duplicate logs
      forwardErrorsToLogs: false,
      forwardConsoleLogs: 'all',
    });

    console.log('DataDog Logs initialized successfully');
  } catch (error) {
    console.error('Failed to initialize DataDog Logs:', error);
  }
};

// Helper functions for logging
export const logInfo = (message: string, context?: object) => {
  datadogLogs.logger.info(message, context);
};

// Utility function to create proper error objects with stack traces
export const createErrorWithStack = (error: any, context?: string): Error => {
  let errorObj: Error;
  
  if (error instanceof Error) {
    errorObj = error;
  } else if (typeof error === 'string') {
    errorObj = new Error(error);
  } else if (error && typeof error === 'object') {
    errorObj = new Error(error.message || 'Unknown error');
    if (error.stack) {
      errorObj.stack = error.stack;
    }
  } else {
    errorObj = new Error(String(error));
  }
  
  // Add context to the error message if provided
  if (context) {
    errorObj.message = `[${context}] ${errorObj.message}`;
  }
  
  return errorObj;
};

// Enhanced error logging with automatic error object creation
export const logErrorWithContext = (message: string, error: any, context?: object, errorContext?: string) => {
  const errorObj = createErrorWithStack(error, errorContext);
  logError(message, errorObj, context);
};

export const logError = (message: string, error?: Error | any, context?: object) => {
  // Ensure we have a proper error object with stack trace
  let errorMessage = '';
  let stackTrace = '';
  
  if (error) {
    if (error instanceof Error) {
      errorMessage = error.message;
      stackTrace = error.stack || '';
      // Mark as handled to prevent duplicate logging
      (error as any).__datadogHandled = true;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object') {
      errorMessage = error.message || error.toString();
      stackTrace = error.stack || '';
      // Mark as handled to prevent duplicate logging
      error.__datadogHandled = true;
    } else {
      errorMessage = String(error);
    }
  }

  // Create a more detailed error context
  const errorContext = {
    error: errorMessage,
    stack: stackTrace,
    errorType: error?.constructor?.name || typeof error,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    // Add source map hints
    sourceMapAvailable: true,
    ...context,
  };

  // Log to DataDog
  datadogLogs.logger.error(message, errorContext);
  
  // Also log to console for debugging
  console.error('DataDog Error Log:', message, errorContext);
  
  // Also add to RUM for better error tracking
  if (error instanceof Error) {
    datadogRum.addError(error, {
      source: 'custom_logging',
      message: message,
      ...context,
    });
  }
};

export const logWarn = (message: string, context?: object) => {
  datadogLogs.logger.warn(message, context);
};

// RUM action tracking
export const trackUserAction = (name: string, attributes?: object) => {
  datadogRum.addAction(name, attributes);
};

// RUM view tracking
export const trackView = (name: string, attributes?: object) => {
  // Note: View tracking is automatically handled by DataDog RUM
  // This function can be used for custom view tracking if needed
  console.log(`View tracked: ${name}`, attributes);
};

// Set user information
export const setUser = (user: { id: string; name?: string; email?: string; [key: string]: any }) => {
  datadogRum.setUser(user);
  datadogLogs.setUser(user);
};

// Set global context
export const setGlobalContext = (context: object) => {
  datadogRum.setGlobalContext(context);
  datadogLogs.setGlobalContext(context);
};

// Test function to verify stack trace logging
export const testStackTraceLogging = () => {
  try {
    // Create a test error with a stack trace
    const testError = new Error('Test error for stack trace verification');
    testError.stack = testError.stack || 'Test stack trace';
    
    logError('Test error logging', testError, {
      test: true,
      timestamp: new Date().toISOString(),
    });
    
    console.log('Test error logged to DataDog. Check your DataDog dashboard for the stack trace.');
  } catch (error) {
    console.error('Failed to test stack trace logging:', error);
  }
};

// Comprehensive test for source map integration
export const testSourceMapIntegration = () => {
  console.log('Testing DataDog source map integration...');
  
  // Test 1: Basic error with stack trace
  const basicError = new Error('Source map test error');
  logError('Source map test - basic error', basicError, { testType: 'source_map_basic' });
  
  // Test 2: Error from a specific function call
  const functionError = new Error('Function call error');
  functionError.stack = `Error: Function call error
    at testSourceMapIntegration (${window.location.origin}/src/utils/datadog.ts:123:45)
    at onClick (${window.location.origin}/src/components/DataDogTest.tsx:15:23)
    at HTMLButtonElement.callCallback (${window.location.origin}/src/components/DataDogTest.tsx:25:67)`;
  
  logError('Source map test - function error', functionError, { testType: 'source_map_function' });
  
  // Test 3: Async error
  setTimeout(() => {
    const asyncError = new Error('Async source map test error');
    logError('Source map test - async error', asyncError, { testType: 'source_map_async' });
  }, 100);
  
  console.log('Source map integration tests completed. Check DataDog dashboard for results.');
}; 